import type { SearchParams } from '@/server/searchParams.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import type { PageWrapperData } from '@/app/common/createPageWrapperData.ts';
import type { PageWrapperDataPayload } from '@/app/api/pageWrapperData/route.ts';
import clientAPICall from '@/app/components/search/common/clientAPICall.ts';
import { API_NAME } from '@/app/components/search/common/apiTypes.ts';
import { isFalsyOrEmpty } from '@/app/common/utils/utility.ts';

type FetchPageWrapperDataProps = {
  searchParams: SearchParams;
  traceParent: string;
  commonData: CommonData;
};

// Fetches fresh page data for the given search params through the BFF route, never through the
// server module: a server action would render the current route again from the server. Returns
// undefined when the call failed or answered with nothing, so each caller decides what to do then
// (revert the URL, keep the current data, log).
export const fetchPageWrapperData = async ({
  searchParams,
  traceParent,
  commonData,
}: FetchPageWrapperDataProps): Promise<PageWrapperData | undefined> => {
  const payload: PageWrapperDataPayload = { searchParams };
  const pageWrapperData = await clientAPICall<PageWrapperDataPayload, PageWrapperData>({
    apiName: API_NAME.PAGE_WRAPPER_DATA,
    payload,
    traceParent,
    commonData,
    // MPH-1410: the answer's commonData is a new pair from Casper, which the page adopts, so the server signal on a
    // soft refresh sent with a stale pair needs no separate renewal.
    ignoreRenewSignal: true,
  });

  return isFalsyOrEmpty(pageWrapperData) ? undefined : pageWrapperData;
};
